import React, {
  useState,
  useRef,
  useCallback,
  ChangeEvent,
  useEffect,
} from "react"
import getValidationErrors from "../../utils/getValidationErrors"

import {
  Container,
  Content,
  Main,
  GroupBox,
  TabsContainer,
  TabButton,
  SmallButton,
  TestCard,
  TestsContainer,
  FieldsContainer,
  ButtonsContainer,
  StyledForm,
  SearchContainer,
  SearchCard,
} from "./styles"

import {
  FiBook,
  FiChevronsDown,
  FiClipboard,
  FiPlus,
  FiSearch,
  FiTrash2,
} from "react-icons/fi"

import { FormHandles } from "@unform/core"
import * as Yup from "yup"

import Sidebar from "../../components/Sidebar"
import Header from "../../components/Header"
import Input from "../../components/InputUnform"
import RegistersButton from "../../components/RegistersButton"
import Pagination from "../../components/Pagination"
import DatePickerInput from "../../components/DatePickerInput"

import IClasses from "../../models/IClasses"
import IClassTests from "../../models/IClassTests"

import { useToast } from "../../hooks/toast"

import api from "../../services/api"
import { uuid } from "uuidv4"
import returnBackendErrors from "../../utils/returnBackendError"

interface RegisterClassFormData {
  description: string
}

const SchoolClass: React.FC = () => {
  const formRef = useRef<FormHandles>(null)
  const searchRef = useRef<FormHandles>(null)
  const serachContainerRef = useRef<HTMLDivElement>(null)

  const { addToast } = useToast()

  const [activeTab, setActiveTab] = useState("Cadastro")
  const [tests, setTests] = useState<IClassTests[]>([])

  const [selectedClass, setSelectedClass] = useState<IClasses | undefined>(
    undefined
  )

  const [classDescription, setClassDescription] = useState("")

  // Campos das avaliações
  const [date, setDate] = useState<Date | undefined>(new Date())
  const [description, setDescription] = useState("")
  const [weight, setWeight] = useState("")

  // Pesquisa
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(20)
  const [classes, setClasses] = useState<IClasses[]>([])
  const [limit, setLimit] = useState(20)

  const handleClear = useCallback((all: boolean) => {
    if (all) {
      formRef.current?.clearField("description")

      formRef.current?.clearField("testDescription")
      formRef.current?.setFieldError("testDescription", "")
      formRef.current?.clearField("weight")
      setDescription("")
      setWeight("")
      setDate(new Date())
      setTests([])

      setClassDescription("")
      setSelectedClass(undefined)
    } else {
      formRef.current?.clearField("testDescription")
      formRef.current?.setFieldError("testDescription", "")
      formRef.current?.clearField("weight")
      setDescription("")
      setWeight("")
      setDate(new Date())
    }
  }, [])

  const handleSearch = useCallback(async () => {
    try {
      searchRef.current?.setErrors({})

      const name = searchRef.current?.getFieldValue("classSearch")

      const response = await api.get("/classes", {
        params: {
          page,
          name,
          limit,
        },
      })

      setClasses(response.data.classes)
      setTotalPages(response.data.totalPages)
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err)

        formRef.current?.setErrors(errors)

        return
      }

      addToast({
        type: "error",
        title: "Erro ao Pesquisar",
        description: "Ocorreu um erro ao pesquisar, tente novamente mais tarde",
      })
    }
  }, [addToast, page, limit])

  const handleSubmit = useCallback(
    async (data: RegisterClassFormData) => {
      try {
        formRef.current?.setErrors({})

        const schema = Yup.object().shape({
          description: Yup.string().required(
            "Descrição da turma é obrigatória"
          ),
        })

        await schema.validate(data, {
          abortEarly: false,
        })

        if (selectedClass) {
          try {
            await api.patch(`/class/${selectedClass.id}`, {
              name: data.description,
              tests: tests.map((test) => {
                test.weight = Number(test.weight)
                return test
              }),
            })
          } catch (error) {
            addToast({
              title: "Erro",
              type: "error",
              description: returnBackendErrors(error),
            })

            return
          }
        } else {
          try {
            await api.post("/class", {
              name: data.description,
              tests: tests.map((test) => {
                test.weight = Number(test.weight)
                return test
              }),
            })
          } catch (error) {
            addToast({
              title: "Erro",
              type: "error",
              description: returnBackendErrors(error),
            })

            return
          }
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err)

          formRef.current?.setErrors(errors)

          return
        }

        addToast({
          type: "error",
          title: "Erro no Cadastro",
          description:
            "Ocorreu um erro ao fazer cadastro, tente novamente mais tarde",
        })
      }
      handleSearch()
      handleClear(true)

      addToast({
        type: "success",
        title: "Parabéns!",
        description: "Cadastro realizado com sucesso",
      })
    },
    [addToast, handleSearch, tests, handleClear, selectedClass]
  )

  const fetchTests = useCallback(async (class_id: string) => {
    const response = await api.get("/class-tests/", {
      params: {
        class_id,
        initialDate: "",
        finalDate: "",
      },
    })
    setTests(response.data.tests)
  }, [])

  const handleSelectClass = useCallback(
    async (schoolClass: IClasses) => {
      setSelectedClass(schoolClass)

      fetchTests(schoolClass.id)

      setActiveTab("Cadastro")
      setClassDescription(schoolClass.name)
    },
    [fetchTests]
  )

  const handlePageClick = useCallback((page: number) => {
    setPage(page)
  }, [])

  const validateTest = useCallback(() => {
    if (description === "") {
      formRef.current?.setFieldError("testDescription", "Descrição obrigatória")

      return false
    }

    return true
  }, [description])

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDescription(event.target.value)
    },
    []
  )

  const handleWeightChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setWeight(event.target.value)
    },
    []
  )

  const handleAddTest = useCallback(async () => {
    if (!validateTest()) {
      return
    }

    let formattedDate = new Date()
    if (date) {
      formattedDate = new Date(date)
    }

    if (!selectedClass) {
      const test: IClassTests = {
        id: uuid(),
        date: formattedDate,
        description,
        weight: Number(weight),
      }

      setTests([...tests, test])

      handleClear(false)
    } else {
      try {
        await api.post("/class-test", {
          date: `${formattedDate.getFullYear()}-${(formattedDate.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${formattedDate
            .getDate()
            .toString()
            .padStart(2, "0")}`,
          description,
          weight: Number(weight),
          class_id: selectedClass.id,
        })

        fetchTests(selectedClass.id)
        handleClear(false)
      } catch (error) {
        addToast({
          title: "Erro",
          type: "error",
          description: returnBackendErrors(error),
        })
      }
    }
  }, [
    fetchTests,
    selectedClass,
    date,
    description,
    weight,
    tests,
    handleClear,
    validateTest,
    addToast,
  ])

  const handleDeleteTest = useCallback(
    async (id: string, class_id?: string) => {
      if (!selectedClass) {
        const testsTemp = [...tests]
        const idx = testsTemp.findIndex((test) => test.id === id)

        if (idx !== -1) {
          testsTemp.splice(idx, 1)
          setTests(testsTemp)
        }
      } else {
        try {
          await api.delete(`/class-test/${id}/${class_id}`)

          fetchTests(selectedClass.id)
          handleClear(false)
        } catch (error) {
          addToast({
            title: "Erro",
            type: "error",
            description: returnBackendErrors(error),
          })
        }
      }
    },
    [fetchTests, tests, selectedClass, handleClear, addToast]
  )

  const handleDeleteClass = useCallback(async () => {
    if (!selectedClass) {
      addToast({
        type: "error",
        title: "Erro ao Excluir",
        description: "Nenhuma classe selecionada para exclusão",
      })
    }

    try {
      await api.delete(`/class/${selectedClass?.id}`)

      handleClear(true)
      handleSearch()

      addToast({
        type: "success",
        title: "Parabéns",
        description: "Classe excluída com sucesso",
      })
    } catch (error) {
      addToast({
        title: "Erro",
        type: "error",
        description: returnBackendErrors(error),
      })
    }
  }, [addToast, handleClear, handleSearch, selectedClass])

  useEffect(() => {
    handleSearch()
  }, [handleSearch])

  useEffect(() => {
    const updateLimit = () => {
      const altura = serachContainerRef.current?.clientHeight
      if (altura) {
        setLimit(Math.floor(altura / 60))
      }
    }

    updateLimit()

    window.addEventListener("resize", updateLimit)

    return () => window.removeEventListener("resize", updateLimit)
  }, [])

  return (
    <Container>
      <Header />

      <Main>
        <Sidebar />

        <Content>
          <h1>Cadastro de Turmas</h1>

          <GroupBox>
            <TabsContainer>
              <TabButton
                isActive={activeTab === "Cadastro"}
                onClick={() => setActiveTab("Cadastro")}
              >
                Cadastro
              </TabButton>
              <TabButton
                isActive={activeTab === "Pesquisa"}
                onClick={() => setActiveTab("Pesquisa")}
              >
                Pesquisa
              </TabButton>
            </TabsContainer>

            {activeTab === "Cadastro" && (
              <StyledForm ref={formRef} onSubmit={handleSubmit}>
                <FieldsContainer>
                  <span style={{ width: "100%" }}>
                    <Input
                      name="description"
                      icon={FiClipboard}
                      type="text"
                      placeholder="Descrição da Turma"
                      containerStyle={{ flex: 1 }}
                      value={classDescription}
                      onChange={(e) => setClassDescription(e.target.value)}
                    />
                  </span>
                </FieldsContainer>

                <h3>Avaliações</h3>

                <FieldsContainer>
                  <span style={{ width: "100%" }}>
                    <Input
                      name="testDescription"
                      icon={FiBook}
                      type="text"
                      placeholder="Descrição da Avaliação"
                      onChange={handleDescriptionChange}
                      containerStyle={{ flex: 1 }}
                    />

                    <Input
                      name="weight"
                      icon={FiChevronsDown}
                      type="text"
                      placeholder="Peso"
                      onChange={handleWeightChange}
                    />

                    <DatePickerInput selected={date} onSelect={setDate} />

                    <SmallButton
                      className="add"
                      type="button"
                      onClick={() => handleAddTest()}
                    >
                      <FiPlus />
                    </SmallButton>
                  </span>
                </FieldsContainer>

                <TestsContainer>
                  {tests.map((test) => (
                    <TestCard key={test.description}>
                      <div>
                        <h4>{test.description}</h4>
                        <p>{`Peso: ${test.weight}`}</p>
                        <p>{`Data: ${new Date(test.date)
                          .getDate()
                          .toString()
                          .padStart(2, "0")}/${(
                          new Date(test.date).getMonth() + 1
                        )
                          .toString()
                          .padStart(2, "0")}/${new Date(
                          test.date
                        ).getFullYear()}`}</p>
                      </div>

                      <FiTrash2
                        onClick={() =>
                          handleDeleteTest(
                            test.id ? test.id : "",
                            selectedClass?.id
                          )
                        }
                      />
                    </TestCard>
                  ))}
                </TestsContainer>

                <ButtonsContainer>
                  <RegistersButton type="submit">Salvar</RegistersButton>
                  <RegistersButton
                    type="button"
                    onClick={() => handleDeleteClass()}
                  >
                    Excluir
                  </RegistersButton>
                  <RegistersButton
                    type="reset"
                    onClick={() => handleClear(true)}
                  >
                    Limpar
                  </RegistersButton>
                </ButtonsContainer>
              </StyledForm>
            )}

            {activeTab === "Pesquisa" && (
              <StyledForm ref={searchRef} onSubmit={() => {}}>
                <FieldsContainer>
                  <span style={{ width: "100%" }}>
                    <Input
                      name="classSearch"
                      type="text"
                      placeholder="Pesquisa por descrição"
                      containerStyle={{ flex: 1 }}
                    />

                    <SmallButton
                      className="search"
                      type="button"
                      onClick={handleSearch}
                    >
                      <FiSearch />
                    </SmallButton>
                  </span>
                </FieldsContainer>

                <SearchContainer ref={serachContainerRef}>
                  {classes.map((schoolClass) => (
                    <SearchCard
                      key={schoolClass.id}
                      onClick={() => handleSelectClass(schoolClass)}
                    >
                      {schoolClass.name}
                    </SearchCard>
                  ))}
                </SearchContainer>

                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageClick={handlePageClick}
                />
              </StyledForm>
            )}
          </GroupBox>
        </Content>
      </Main>
    </Container>
  )
}

export default SchoolClass
